import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAdmin } from '@/hooks/useAdmin';

export type ViewMode = 'admin' | 'consultant';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isAdminView: boolean;
  isConsultantView: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

const STORAGE_KEY = 'activity-tracker-view-mode';

interface ViewModeProviderProps {
  children: ReactNode;
}

export function ViewModeProvider({ children }: ViewModeProviderProps) {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Default to admin view for admins, consultant for non-admins
    const saved = localStorage.getItem(STORAGE_KEY) as ViewMode | null;
    return saved || 'admin';
  });

  // Reset to admin view if user is not admin
  useEffect(() => {
    if (!adminLoading) {
      if (!isAdmin && viewMode === 'admin') {
        setViewModeState('consultant');
        localStorage.setItem(STORAGE_KEY, 'consultant');
      } else if (isAdmin && !localStorage.getItem(STORAGE_KEY)) {
        // First time admin enters - default to admin view
        setViewModeState('admin');
        localStorage.setItem(STORAGE_KEY, 'admin');
      }
    }
  }, [isAdmin, adminLoading, viewMode]);

  const setViewMode = (mode: ViewMode) => {
    // Only allow admins to switch modes
    if (isAdmin) {
      setViewModeState(mode);
      localStorage.setItem(STORAGE_KEY, mode);
    }
  };

  const value: ViewModeContextType = {
    viewMode,
    setViewMode,
    isAdminView: viewMode === 'admin',
    isConsultantView: viewMode === 'consultant',
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
}

export function useViewMode() {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
}
